/**
 * Hero video — starts on its own, muted, over the portrait, with a button that invites the
 * visitor to turn the sound on.
 *
 * Two sources behind one control:
 *  - a self-hosted <video> (the real thing): `autoplay muted playsinline` in the markup, so it
 *    starts before any JS, and this only adds the sound toggle;
 *  - a YouTube id (a stand-in until the file exists): a muted background iframe, driven over
 *    YouTube's postMessage protocol — no API script needed for `mute`/`unMute`/`seekTo`.
 *
 * The first time the sound goes on, the video jumps back to the start, so nobody hears the
 * intro from the middle. After that the pill collapses into a small round mute toggle.
 * Under prefers-reduced-motion nothing plays until the button is pressed, and then with sound.
 */
const YT_ORIGIN = 'https://www.youtube-nocookie.com';

export function heroVideo() {
  const hosts = document.querySelectorAll('[data-hero-video]');
  if (!hosts.length) return;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  hosts.forEach((host) => {
    const button = host.querySelector('[data-hero-unmute]');
    const video = host.querySelector('video');
    const markPlaying = () => host.classList.add('is-playing');
    let setSound = null;

    if (video) {
      video.addEventListener('playing', markPlaying);
      if (reduceMotion) video.pause();
      else video.play().catch(() => {});
      setSound = (on, restart) => {
        video.muted = !on;
        if (restart) video.currentTime = 0;
        video.play().catch(() => {});
      };
    } else if (host.dataset.youtubeId) {
      const id = host.dataset.youtubeId;
      const stage = host.querySelector('[data-hero-stage]');
      let iframe = null;
      const post = (message) => iframe?.contentWindow?.postMessage(JSON.stringify(message), YT_ORIGIN);
      const command = (func, args = []) => post({ event: 'command', func, args });

      const mount = (withSound) => {
        const params = new URLSearchParams({
          autoplay: '1', mute: withSound ? '0' : '1', loop: '1', playlist: id, controls: '0',
          rel: '0', playsinline: '1', modestbranding: '1', iv_load_policy: '3', disablekb: '1',
          enablejsapi: '1', origin: window.location.origin,
        });
        iframe = document.createElement('iframe');
        iframe.src = `${YT_ORIGIN}/embed/${encodeURIComponent(id)}?${params}`;
        iframe.title = host.dataset.title || 'Video';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
        iframe.tabIndex = -1;
        iframe.addEventListener('load', () => {
          // Subscribing is what makes the player report its state back to us.
          post({ event: 'listening', id: 'hero', channel: 'widget' });
          // YouTube paints black before the first frame; if no state ever arrives, fade in anyway.
          setTimeout(markPlaying, 3000);
        });
        stage.appendChild(iframe);
      };

      window.addEventListener('message', (event) => {
        if (event.origin !== YT_ORIGIN || !iframe || event.source !== iframe.contentWindow) return;
        let data;
        try { data = JSON.parse(event.data); } catch { return; }
        const state = data.event === 'onStateChange' ? data.info : data.info?.playerState;
        if (state === 1) markPlaying();
      });

      if (!reduceMotion) mount(false);
      setSound = (on, restart) => {
        if (!iframe) { mount(on); return; }
        if (restart) command('seekTo', [0, true]);
        command(on ? 'unMute' : 'mute');
        if (on) command('setVolume', [100]);
        command('playVideo');
      };
    }

    if (!button || !setSound) return;
    let first = true;
    button.addEventListener('click', () => {
      const on = button.getAttribute('aria-pressed') !== 'true';
      setSound(on, first && on);
      if (on) first = false;
      button.setAttribute('aria-pressed', String(on));
      button.setAttribute('aria-label', on ? 'Ztlumit zvuk' : 'Zapnout zvuk');
      button.classList.add('is-on');
    });
  });
}
